/**
 * 分时段生效时间配置组件
 *
 * 职责：
 * 1. 以 TimePicker.RangePicker 列表形式编辑多组生效时间段
 * 2. 支持添加、删除、修改时间段，并通过 saveTimeLimit 回调同步给父组件
 * 3. 由父组件（EditForm）统一校验时间段是否重叠
 *
 * 注意：defaultValue 仅在校准时生效，编辑后通过 onChange 实时回传，保持受控与非受控的兼容。
 */
import React, { useCallback, useMemo } from 'react';
import { TimePicker, Row, Col } from 'antd';
import moment from 'moment';

interface TimeLimitData {
  id: string | number;
  value: any;
  errorMes: any;
}

interface TimeLimitProps {
  initialValue: TimeLimitData[];
  saveTimeLimit: AnyFunc;
}

interface TimeLimitItemProps {
  item: TimeLimitData;
  index: number;
  showDelete: boolean;
  saveTimeLimit: AnyFunc;
}

/**
 * 时间选择浮层挂载到抽屉根容器。
 * 不挂到触发节点父级，避免 picker 打开/关闭改变表单项尺寸时触发 ResizeObserver loop。
 */
const getDrawerPopupContainer = (triggerNode: HTMLElement) =>
  (triggerNode.closest('.map-editor-drawer-container') as HTMLElement | null) ??
  triggerNode.parentElement ??
  triggerNode;

/** 单个时间段选择行：独立 memo，避免父级重渲染时整列表重建 */
const TimeLimitItem = React.memo(
  function TimeLimitItem({ item, index, showDelete, saveTimeLimit }: TimeLimitItemProps) {
    /** RangePicker 默认值：只在首次挂载时使用，但用 memo 避免重复创建 moment 对象 */
    const defaultValue = useMemo(() => {
      return item.value
        ? item.value.map((time: string) => moment(time, 'HH:mm'))
        : null;
    }, [item.value]);

    /** 时间段变更 */
    const handleEdit = useCallback(
      (timeString: [string, string]) => {
        const [startTime, endTime] = timeString;
        if (startTime.length > 0 && endTime.length > 0) {
          saveTimeLimit('edit', { ...item, value: timeString });
        } else {
          saveTimeLimit('edit', { ...item, value: null });
        }
      },
      [item, saveTimeLimit]
    );

    /** 新增一行 */
    const handleAdd = useCallback(() => {
      saveTimeLimit('add');
    }, [saveTimeLimit]);

    /** 删除当前行 */
    const handleDelete = useCallback(() => {
      saveTimeLimit('del', item);
    }, [item, saveTimeLimit]);

    const rowStyle = useMemo(
      () => ({ marginTop: index !== 0 ? '10px' : '0px' }),
      [index]
    );

    return (
      <Row align="middle" style={rowStyle}>
        <Col span={18} key="time-picker">
          <TimePicker.RangePicker
            style={{ width: '100%' }}
            format={'HH:mm'}
            defaultValue={defaultValue}
            getPopupContainer={getDrawerPopupContainer}
            onChange={(time: any, timeString: [string, string]) =>
              handleEdit(timeString)
            }
          />
        </Col>
        <Col span={3} className="operate-effectTime add" key="addBtn">
          <span onClick={handleAdd}>添加</span>
        </Col>
        {showDelete && (
          <Col span={3} className="operate-effectTime del" key="delBtn">
            <span onClick={handleDelete}>删除</span>
          </Col>
        )}
        {item.errorMes && <div className="error-mes">{item.errorMes}</div>}
      </Row>
    );
  }
);

const TimeLimit = ({ initialValue, saveTimeLimit }: TimeLimitProps) => {
  const showDelete = (initialValue?.length ?? 0) > 1;

  return (
    <>
      {initialValue?.map((item: TimeLimitData, index: number) => {
        return (
          <TimeLimitItem
            key={item.id}
            item={item}
            index={index}
            showDelete={showDelete}
            saveTimeLimit={saveTimeLimit}
          />
        );
      })}
    </>
  );
};

export default React.memo(TimeLimit);
